<template>
  <v-card>
    <v-container class="py-0">
      <v-card-title class="text-h5 mt-2 px-0">
        <v-row class="py-0" align="center" no-gutters>
          <v-col class="pb-2 pt-0">
            {{ tituloDetalle }}
            {{ financiadorNombre ? ` - ${financiadorNombre}` : "" }}
          </v-col>
          <v-col cols="auto" class="pt-1 pb-0">
            <v-card-actions class="px-0 py-0">
              <v-btn
                color="primary"
                @click="closeDialog"
                :icon="closeIcon"
                variant="text"
                id="v-btn-close-detalle-importacion"
              >
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text class="px-0 pt-3">
        <!-- CONTENIDO PRINCIPAL: Tabla de detalles -->
        <v-data-table
          :headers="detallesHeaders"
          :items="detallesImportacion"
          :loading="loadingDetalles"
          :items-per-page="10"
          density="compact"
          class="elevation-1"
        >
          <template v-slot:[`item.fechaCreacion`]="{ item }">
            {{ formatDate(item.fechaCreacion) }}
          </template>
          <template v-slot:[`item.deshabilitado`]="{ item }">
            <v-chip
              :color="item.deshabilitado ? 'error' : 'success'"
              variant="flat"
              size="small"
            >
              {{ item.deshabilitado ? "Deshabilitado" : "Habilitado" }}
            </v-chip>
          </template>
          
        </v-data-table>

        <!-- ACCIONES/BOTONES dentro del v-card-text -->
        <v-card-actions class="justify-end mt-3 px-0">
          <v-btn
            color="primary"
            @click="exportarDetalle"
            :disabled="!detallesImportacion || detallesImportacion.length === 0"
            :prepend-icon="exportIcon"
            variant="outlined"
            id="v-btn-exportar-detalle"
          >
            Exportar detalle
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-container>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import enums from "@/utils/enums/index";
import helpersExcel from "@/utils/helpers/importExportExcel.js";

export default {
  name: "VerDetalleImportacion",
  props: {
    importacionItem: {
      type: Object,
      required: true,
    },
    tipoEntidad: {
      type: String,
      required: true,
      validator: (value) =>
        ["Farmacia", "Entidad emisora", "Medico emisor"].includes(value),
    },
    financiadorId: {
      type: Number,
      required: true,
    },
    financiadorNombre: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      closeIcon: enums.icons.CLOSE_ICON,
      exportIcon: enums.icons.DOCUMENT,
      detallesImportacion: [],
      loadingDetalles: false,
    };
  },
  computed: {
    tituloDetalle() {
      const base = this.tipoEntidad === "Medico emisor" ? "Detalles de importación por Matrícula - Médico Emisor" : `Detalles de importación - ${this.tipoEntidad}`;
      return base;
    },
    detallesHeaders() {
      const allHeaders = [
        // Priorizar matrícula y provincia para médicos emisores
        {
          title: "Matrícula",
          value: "matricula",
          align: "center",
          sortable: true,
          entidades: ["Medico emisor"],
        },
        {
          title: "Provincia",
          value: "provincia",
          align: "start",
          sortable: true,
          entidades: ["Medico emisor"],
        },
        {
          title: "CUIT",
          value: "cuit",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora", "Medico emisor"],
        },
        {
          title: "Nombre",
          value: "nombre",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora"],
        },
        {
          title: "Nombre y Apellido",
          value: "nombre",
          align: "start",
          sortable: true,
          entidades: ["Medico emisor"],
        },
        {
          title: "Razón Social",
          value: "razonSocial",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora"],
        },
        {
          title: "Tipo documento",
          value: "tipoDoc",
          align: "center",
          sortable: true,
          entidades: ["Medico emisor"],
        },
        {
          title: "Número documento",
          value: "nroDoc",
          align: "center",
          sortable: true,
          entidades: ["Medico emisor"],
        },
        {
          title: "Email",
          value: "email",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora", "Medico emisor"],
        },
        {
          title: "Usuario",
          value: "usuarioNombre",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora"],
        },
        {
          title: "Fecha creación",
          value: "fechaCreacion",
          align: "center",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora", "Medico emisor"],
        },
        {
          title: "Estado",
          value: "deshabilitado",
          align: "center",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora", "Medico emisor"],
        },
        {
          title: "Teléfono",
          value: "telefono",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora"],
        },
        {
          title: "Dirección",
          value: "direccion",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Medico emisor"],
        },
        {
          title: "Código",
          value: "codigo",
          align: "start",
          sortable: true,
          entidades: ["Farmacia"],
        },
        {
          title: "Provincia",
          value: "provincia",
          align: "start",
          sortable: true,
          entidades: ["Farmacia"],
        },
        {
          title: "Observación",
          value: "observacion",
          align: "start",
          sortable: true,
          entidades: ["Farmacia", "Entidad emisora"],
        },
      ];

      return allHeaders.filter((header) =>
        header.entidades.includes(this.tipoEntidad)
      );
    },
  },
  async created() {
    await this.loadDetalles();
  },
  methods: {
    ...mapActions({
      setAlert: "user/setAlert",
      getDetalleImportacion: "farmacia/getDetalleImportacion",
    }),
    async loadDetalles() {
      this.loadingDetalles = true;
      try {
        const response = await this.getDetalleImportacion({
          importacionId: this.importacionItem.importacionId,
          financiadorId: this.financiadorId,
          tipoEntidad: this.tipoEntidad,
        });

        // Mapear las propiedades del DTO de C# al formato camelCase del frontend
        this.detallesImportacion = (response || []).map((detalle) => ({
          cuit: detalle.Cuit || detalle.cuit,
          nombre: detalle.Nombre || detalle.nombre,
          razonSocial: detalle.RazonSocial || detalle.razonSocial,
          email: detalle.Email || detalle.email,
          usuarioNombre: detalle.UsuarioNombre || detalle.usuarioNombre,
          fechaCreacion: detalle.FechaCreacion || detalle.fechaCreacion,
          deshabilitado: detalle.Deshabilitado || detalle.deshabilitado,
          // Campos específicos
          telefono: detalle.Telefono || detalle.telefono,
          direccion: detalle.Direccion || detalle.direccion,
          observacion: detalle.Observacion || detalle.observacion,
          codigo: detalle.Codigo || detalle.codigo,
          provincia: detalle.Provincia || detalle.provincia,
          nroDoc: detalle.NroDoc || detalle.nroDoc,
          tipoDoc: detalle.TipoDoc || detalle.tipoDoc,
          tipoPrestador: detalle.TipoPrestador || detalle.tipoPrestador,
          matricula: detalle.Matricula || detalle.matricula,
        }));
      } catch (error) {
        console.error("Error loading detalles:", error);
        this.setAlert({
          type: "error",
          message: "Error al cargar los detalles de importación",
        });
      } finally {
        this.loadingDetalles = false;
      }
    },
    closeDialog() {
      this.$emit("closeDialog");
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    exportarDetalle() {
      if (!this.detallesImportacion || this.detallesImportacion.length === 0) {
        this.setAlert({
          type: "warning",
          message: "No hay datos para exportar",
        });
        return;
      }

      try {
        // Preparar datos para exportación
        const datosExportacion = this.detallesImportacion.map((item) => {
          const fila = {};
          
          // Mapear solo las columnas visibles según el tipo de entidad
          this.detallesHeaders.forEach((header) => {
            let valor = item[header.value];
            
            // Formatear valores especiales
            if (header.value === "fechaCreacion") {
              valor = this.formatDate(valor);
            } else if (header.value === "deshabilitado") {
              valor = valor ? "Deshabilitado" : "Habilitado";
            }
            
            fila[header.title] = valor || "";
          });

          return fila;
        });

        const tipoEntidadLabel = this.tipoEntidad === "Medico emisor" ? "Médico Emisor (por Matrícula)" : this.tipoEntidad;
        const nombreArchivo = `Detalle Importación ${tipoEntidadLabel}${this.financiadorNombre ? ` - ${this.financiadorNombre}` : ""}`;
        
        const formato = {
          filas: datosExportacion,
          hoja: `Detalle ${tipoEntidadLabel}`,
        };

        helpersExcel.excelExport(formato, nombreArchivo);

        this.setAlert({
          type: "success",
          message: "Detalle exportado exitosamente",
        });
      } catch (error) {
        console.error("Error al exportar detalle:", error);
        this.setAlert({
          type: "error",
          message: "Error al exportar el detalle",
        });
      }
    },
  },
};
</script>

<style scoped></style>